import React, { useState } from 'react';
import { HelpCircle, ChevronDown, MessageCircle } from 'lucide-react';

const translations = {
  de: {
    title: 'F.A.Q.',
    subtitle: 'Häufig gestellte Fragen',
    faqs: [
      {
        question: 'Wie kann ich weltwelt365 nutzen?',
        answer: 'Nach der Anmeldung erhalten Sie sofortigen Zugang zu allen Spielen auf unserer Plattform. Loggen Sie sich einfach ein und beginnen Sie zu spielen - keine zusätzlichen Downloads erforderlich.',
      },
      {
        question: 'Welche Spiele bieten Sie an?',
        answer: 'Wir bieten über 90 verschiedene Spiele in verschiedenen Kategorien an, darunter Action, Abenteuer, Puzzle, Arcade und mehr. Alle Spiele sind sorgfältig ausgewählt, um höchste Qualität zu gewährleisten.',
      },
      {
        question: 'Wie viele Spiele kann ich spielen?',
        answer: 'Als Abonnent haben Sie unbegrenzten Zugang zu allen Spielen in unserer Bibliothek. Sie können so viele Spiele spielen, wie Sie möchten, wann immer Sie möchten.',
      },
      {
        question: 'Kann ich die Spiele auf mein Handy herunterladen?',
        answer: 'Unsere Spiele laufen auf HTML5 und erfordern keine Downloads. Sie können direkt in Ihrem Browser spielen, was Speicherplatz spart und ein sofortiges Spielerlebnis ermöglicht.',
      },
      {
        question: 'Auf welchem Gerät kann ich diese Spiele spielen?',
        answer: 'Sie können auf jedem Gerät mit einem modernen Webbrowser spielen - Smartphones, Tablets, Laptops und Desktop-Computer. Unsere Plattform ist vollständig responsiv.',
      },
      {
        question: 'Kann ich mein Konto auf mehreren Geräten verwenden?',
        answer: 'Ja! Sie können sich mit Ihrem Konto auf verschiedenen Geräten anmelden. Ihr Fortschritt und Ihre Einstellungen werden synchronisiert.',
      },
      {
        question: 'Kann ich unterwegs spielen?',
        answer: 'Absolut! Solange Sie eine Internetverbindung haben, können Sie überall und jederzeit auf unsere Spiele zugreifen - perfekt für unterwegs.',
      },
      {
        question: 'Was passiert, wenn mein Telefon nicht kompatibel ist?',
        answer: 'Unsere HTML5-Spiele sind mit den meisten modernen Geräten und Browsern kompatibel. Bei Problemen kontaktieren Sie bitte unseren Kundenservice, der Ihnen gerne weiterhilft.',
      },
    ],
  },
  en: {
    title: 'F.A.Q.',
    subtitle: 'Frequently Asked Questions',
    faqs: [
      {
        question: 'How can I use weltwelt365?',
        answer: 'After signing up, you get instant access to all games on our platform. Simply log in and start playing - no additional downloads required.',
      },
      {
        question: 'What games do you offer?',
        answer: 'We offer over 90 different games in various categories including action, adventure, puzzle, arcade and more. All games are carefully selected to ensure the highest quality.',
      },
      {
        question: 'How many games can I play?',
        answer: 'As a subscriber, you have unlimited access to all games in our library. You can play as many games as you want, whenever you want.',
      },
      {
        question: 'Can I download the games to my phone?',
        answer: 'Our games run on HTML5 and require no downloads. You can play directly in your browser, which saves storage space and allows for instant gameplay.',
      },
      {
        question: 'What device can I play these games on?',
        answer: 'You can play on any device with a modern web browser - smartphones, tablets, laptops and desktop computers. Our platform is fully responsive.',
      },
      {
        question: 'Can I use my account on multiple devices?',
        answer: 'Yes! You can log into your account on different devices. Your progress and settings will be synchronized.',
      },
      {
        question: 'Can I play on the go?',
        answer: 'Absolutely! As long as you have an internet connection, you can access our games anywhere, anytime - perfect for on the go.',
      },
      {
        question: 'What happens if my phone is not compatible?',
        answer: 'Our HTML5 games are compatible with most modern devices and browsers. If you experience any issues, please contact our customer service who will be happy to help.',
      },
    ],
  }
};

function FAQ({ language }) {
  const t = translations[language];
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="relative py-24 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/50 to-transparent"></div>
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 border border-secondary/30 rounded-full mb-6">
            <HelpCircle className="w-5 h-5 text-secondary" />
            <span className="text-secondary text-sm font-body font-semibold tracking-wide">{t.subtitle}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-4">
            {t.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary via-secondary to-accent mx-auto rounded-full"></div>
        </div>

        {/* FAQ Accordion */}
        <div className="space-y-4">
          {t.faqs.map((faq, index) => (
            <div
              key={index}
              className={`relative rounded-xl overflow-hidden transition-all duration-300 ${
                openIndex === index 
                  ? 'bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/30' 
                  : 'bg-surface/50 border border-slate-700 hover:border-slate-600'
              }`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center text-white font-heading font-bold text-sm">
                    {index + 1}
                  </span>
                  <span className="font-body font-semibold text-lg text-white pr-4">
                    {faq.question}
                  </span>
                </div>
                <ChevronDown 
                  className={`w-6 h-6 text-slate-400 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180 text-accent' : ''
                  }`}
                />
              </button>
              
              <div 
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-6 pl-[4.5rem]">
                  <p className="text-slate-400 font-body text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Contact prompt */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-surface/50 border border-slate-700 rounded-full">
            <MessageCircle className="w-5 h-5 text-accent" />
            <span className="text-slate-400 font-body">
              {language === 'de' 
                ? 'Haben Sie weitere Fragen? Kontaktieren Sie uns!' 
                : 'Have more questions? Contact us!'}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FAQ;

